import React, { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import clsx from 'clsx'
import useScreen from '../../hooks/useScreen'
import useAuth from '../../hooks/useAuth'
import useRequest from '../../hooks/useRequest'
import useUsers from '../../hooks/useUsers'
import AppRoutes from '../../routes/appRoutes'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import Button from '../../components/Button/Button'
import useIntercom from '../../hooks/useIntercom'
import {
    CalendarIcon,
    DocumentAddIcon,
    DocumentReportIcon,
    DocumentTextIcon,
} from '@heroicons/react/outline'
import Stats from './components/Stat'
import {
    fleetColumns,
    formatAssetList,
    formatDashboardExport,
    fleetExportDataTittles,
} from '../../helpers/tableUtils'
import Table from '../../components/Table/Table'
import ExportDataButton from '../../components/ExportData/ExportDataButton'
import {
    getDashboardData,
    saveDashboardData,
} from '../../../../indexedDb/dashboard'
import useFleet from '../../hooks/useFleet'
import { cacheInitialData } from '../../utils/cacheInitialData'
import { useNetworkStatus } from '../../utils/networkStatus'
import { hasPendingRequests } from '../../utils/pendingRequestHelpers'
import MessageTimeSinceLastUpdate from '../../components/MessageTimeSinceLastUpdate/MessageTimeSinceLastUpdate'
import { useFetchState } from '../../context/FetchStateContext'

const DashboardPage = () => {
    const { user, impersonated } = useAuth()
    const { isMobile, isTablet } = useScreen()
    const { loadIntercom } = useIntercom()
    const [dashboardData, setDashboardData] = useState(null)
    const { isOffline } = useNetworkStatus()
    const [hasPending, setHasPending] = useState(false)
    const { setIsFetching, setDataFetched } = useFetchState()

    useEffect(() => {
        if (!isOffline) {
            loadIntercom()
        } else {
            if (window.Intercom) {
                window.Intercom('shutdown')
            }
        }
    }, [isOffline])

    const fetchPendingRequestsStatus = async () => {
        try {
            const result = await hasPendingRequests()
            setHasPending(result)
        } catch (error) {
            console.error('Error checking pending requests:', error)
        }
    }

    const handleIndexedDBChange = () => {
        fetchPendingRequestsStatus()
    }

    useEffect(() => {
        window.addEventListener('indexedDBChange', handleIndexedDBChange)
        return () => {
            window.removeEventListener('indexedDBChange', handleIndexedDBChange)
        }
    }, [])

    useEffect(() => {
        fetchPendingRequestsStatus()
    }, [])

    const {
        changeNeedsUpdate,
        needsUpdateDashboard,
        needsUpdateRequest,
    } = useRequest()
    const { getDashboardInfo } = useUsers()

    const { dashboard } = user

    const { handleSearchFleet, fleetFilters } = useFleet()

    useEffect(() => {
        if (!isOffline && user && needsUpdateDashboard) {
            setIsFetching(true)
            const userId = user.id
            const locPrefs = user?.locationPreferences || []
            const locPreferencesId = locPrefs.map(item => `${item?.id}`)

            cacheInitialData(
                userId,
                locPreferencesId,
                handleSearchFleet,
                fleetFilters,
                setIsFetching,
                setDataFetched,
            )
        }
    }, [user, isOffline, needsUpdateDashboard])

    useEffect(() => {
        if (needsUpdateDashboard) {
            const userId = user?.id
            getDashboardInfo({ userId, impersonated })
            const needsUpdate = dashboard?.maintenanceMode && !impersonated
            changeNeedsUpdate({
                dashboard: needsUpdate,
                request: needsUpdateRequest,
            })
        }
    }, [needsUpdateDashboard, isOffline, impersonated])

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (!isOffline && dashboard) {
                    const dashboardData = {
                        activePMCount: dashboard.activePMCount,
                        activeRequestCount: dashboard.activeRequestCount,
                        assetUnderRepairCount: dashboard.assetUnderRepairCount,
                        assetUnderRepair: dashboard?.assetUnderRepair || [],
                        onHoldPMCount: dashboard.onHoldPMCount,
                        onHoldRequestCount: dashboard.onHoldRequestCount,
                        openEstimateCount: dashboard.openEstimateCount,
                        totalAssetsCount: dashboard.totalAssetsCount,
                    }

                    const lastTimeUpdated = Date.now()
                    localStorage.setItem(
                        'lastTimeUpdated',
                        lastTimeUpdated.toString(),
                    )
                    setDashboardData(dashboardData)
                    await saveDashboardData(dashboardData)
                } else {
                    const offlineData = await getDashboardData()
                    setDashboardData(offlineData || [])
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error)
            }
        }

        fetchDashboardData()
    }, [dashboard, isOffline])

    const rows = formatAssetList(dashboardData?.assetUnderRepair || [])

    const dashboardExportData = formatDashboardExport(
        dashboardData?.assetUnderRepair || [],
    )

    const requestHighligth = dashboardData?.onHoldRequestCount > 0
    const pmHighligth = dashboardData?.onHoldPMCount > 0
    const estimateHighligth = dashboardData?.openEstimateCount > 0
    return (
        <ContentLayout>
            <MainHeading
                title="Welcome back!"
                actions={[
                    <NavLink
                        key="welcomeBackButton"
                        to={AppRoutes.createRequestStepper()}
                    >
                        <Button
                            className="md:w-auto w-full"
                            text="Create Request"
                            variant="primary"
                            icon={
                                <DocumentAddIcon className="text-yellow stroke-current stroke-2 w-6 h-6 mr-2" />
                            }
                        />
                    </NavLink>,
                ]}
            />

            <div className="w-full h-auto flex flex-col items-start justify-start md:px-6 md:py-8 p-4 gap-y-8">
                <div className="w-full flex lg:flex-row flex-col items-center justify-between gap-4 p-0">
                    <Stats
                        title="Repair Requests"
                        icon={
                            <div
                                className={clsx(
                                    'w-full h-full flex items-center justify-center',
                                    requestHighligth
                                        ? 'bg-error'
                                        : 'bg-black80',
                                )}
                            >
                                <DocumentReportIcon className="w-5 h-5 text-white stroke-current stroke-2" />
                            </div>
                        }
                        items={[
                            {
                                title: 'Active Repair',
                                count: dashboardData?.activeRequestCount,
                                link: {
                                    pathname: AppRoutes.requests(),
                                    search:
                                        '?type=Repair&activeTab=activeRequests',
                                    state: { filterByType: 'Repair' },
                                },
                                isHighlighted: false,
                            },
                            {
                                title: 'Awaiting PO',
                                count: dashboardData?.onHoldRequestCount,
                                link: {
                                    pathname: AppRoutes.requests(),
                                    search:
                                        '?status=Awaiting PO&activeTab=pastRequests',
                                    state: { filterByType: 'Awaiting PO' },
                                },
                                isHighlighted: requestHighligth,
                            },
                        ]}
                        to={`${AppRoutes.requests()}?activeTab=activeRequests`}
                        showAlert={hasPending}
                        isOffline={isOffline}
                    />
                    <Stats
                        title="PM Work Orders"
                        icon={
                            <div
                                className={clsx(
                                    'w-full h-full flex items-center justify-center',
                                    pmHighligth ? 'bg-error' : 'bg-black80',
                                )}
                            >
                                <CalendarIcon className="w-5 h-5 text-white stroke-current stroke-2" />
                            </div>
                        }
                        items={[
                            {
                                title: 'Active PM',
                                count: dashboardData?.activePMCount,
                                link: {
                                    pathname: AppRoutes.plannedMaintenance(),
                                    search: '?activeTab=activeRequests',
                                },
                                isHighlighted: false,
                            },
                            {
                                title: 'Awaiting PO',
                                count: dashboardData?.onHoldPMCount,
                                link: {
                                    pathname: AppRoutes.plannedMaintenance(),
                                    search:
                                        '?status=Awaiting PO&activeTab=pastRequests',
                                    state: { filterByStatus: 'Awaiting PO' },
                                },
                                isHighlighted: pmHighligth,
                            },
                        ]}
                        to={`${AppRoutes.plannedMaintenance()}?activeTab=activeRequests`}
                        emptyAlert={hasPending}
                        isOffline={isOffline}
                    />
                    <Stats
                        title="Service Estimates"
                        icon={
                            <div
                                className={clsx(
                                    'w-full h-full flex items-center justify-center',
                                    estimateHighligth
                                        ? 'bg-error'
                                        : 'bg-black80',
                                )}
                            >
                                <DocumentTextIcon className="w-5 h-5 text-white stroke-current stroke-2" />
                            </div>
                        }
                        items={[
                            {
                                title: 'Awaiting Approval',
                                count: dashboardData?.openEstimateCount,
                                link: {
                                    pathname: AppRoutes.requests(),
                                    search:
                                        '?status=Awaiting Approval&activeTab=estimateRequests',
                                    state: {
                                        filterByStatus: 'Awaiting Approval',
                                    },
                                },
                                isHighlighted: estimateHighligth,
                            },
                        ]}
                        to={`${AppRoutes.requests()}?activeTab=estimateRequests`}
                        emptyAlert={hasPending}
                        isOffline={isOffline}
                    />
                </div>
            </div>
            <div
                className={clsx(
                    'w-full flex-grow h-full  lg:px-6 p-0',
                    (isMobile || isTablet) && 'dashboardLayout',
                )}
            >
                <Table
                    isDashboardPage
                    hasSearch={false}
                    columns={fleetColumns}
                    data={rows}
                    totalResults={rows?.length}
                    type="scroll"
                    headerTitle="Asset Under Repair"
                    headerSubtitle={
                        <span className="font-normal">
                            You have a total of{' '}
                            <b className="text-black90">
                                {dashboardData?.assetUnderRepairCount}
                            </b>{' '}
                            of{' '}
                            <b className="text-black90">
                                {dashboardData?.totalAssetsCount}
                            </b>{' '}
                            assets in an active state of repair{' '}
                            {"(this does not include PM's)"}
                        </span>
                    }
                    actions={[
                        <ExportDataButton
                            key="exportData"
                            text="Download Data"
                            fileName="Dashboard Table.xlsx"
                            sheetName="FleetTable"
                            data={dashboardExportData}
                            tittles={fleetExportDataTittles}
                            className="h-12 md:w-auto w-full"
                        />,
                        <Link
                            key="viewAllAssets"
                            className="h-12 w-full md:w-auto"
                            to={AppRoutes.fleet()}
                        >
                            <Button
                                text="View All Assets"
                                variant="tertiary"
                                className="h-12 w-full md:w-auto"
                            />
                        </Link>,
                    ]}
                />
            </div>
            {isOffline && (
                <div className="fixed bottom-0 right-0 w-96 p-0">
                    <MessageTimeSinceLastUpdate page="dashboard" />
                </div>
            )}
        </ContentLayout>
    )
}

export default DashboardPage
